import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, Route as RouteIcon, X, Paperclip, Download, Maximize2 } from "lucide-react";
import type { LogEntry } from "@/lib/types";
import { tokenUrl } from "@/lib/api";
import { formatBytes } from "@/lib/format";
import { t, dateLocale } from "@/lib/i18n";
import { LevelBadge } from "./LevelBadge";

type LogDetailDrawerProps = {
  log: LogEntry | null;
  onClose: () => void;
  onFilterByClient: (client: string) => void;
  onOpenTrace: (traceId: string) => void;
};

export function LogDetailDrawer({ log, onClose, onFilterByClient, onOpenTrace }: LogDetailDrawerProps) {
  // Großansicht (Lightbox) für Bild/Video. Beim Wechsel des Logs zurücksetzen.
  const [lightbox, setLightbox] = useState(false);
  useEffect(() => setLightbox(false), [log?.id]);

  useEffect(() => {
    if (!log) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Escape schließt zuerst die Großansicht, dann erst das Drawer.
      if (lightbox) setLightbox(false);
      else onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [log, onClose, lightbox]);

  if (!log) return null;

  const hasPayload = log.id != null && !!log.payload_mime;
  const payloadSrc = hasPayload ? tokenUrl(`/api/logs/${log.id}/payload`) : "";

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-foreground/20 backdrop-blur-[1px]"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-label={t("Log-Details")}
        // Mobil: Vollbild-Sheet (inset-0) mit Safe-Areas; Desktop: 520px-Drawer rechts.
        className="fixed inset-0 z-50 flex w-full flex-col bg-card pb-safe pt-safe md:inset-y-0 md:left-auto md:right-0 md:w-[min(520px,100vw)] md:border-l md:border-border md:shadow-2xl"
      >
        <header className="flex items-center justify-between border-b border-border py-2 pl-4 pr-2 md:py-3 md:pr-4">
          <div className="flex items-center gap-2">
            <LevelBadge level={log.level} />
            <h2 className="text-sm font-medium">{t("Log-Details")}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("Schließen")}
            className="flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground md:h-8 md:w-8"
          >
            <X className="h-5 w-5 md:h-4 md:w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-auto p-4">
          <div className="mb-4 whitespace-pre-wrap break-words font-mono text-[13px] leading-relaxed md:text-sm">
            {log.message}
          </div>

          <dl className="grid grid-cols-[84px_1fr] gap-x-3 gap-y-2 text-xs sm:grid-cols-[110px_1fr] sm:gap-y-1.5">
            <Row label={t("Zeit")}>{new Date(log.timestamp).toLocaleString(dateLocale())}</Row>
            <Row label={t("Client")}>
              <button
                type="button"
                onClick={() => onFilterByClient(log.client_id)}
                className="text-primary hover:underline"
              >
                {log.client_id}
              </button>
            </Row>
            {log.service ? <Row label={t("Service")}>{log.service}</Row> : null}
            {log.host ? <Row label={t("Host")}>{log.host}</Row> : null}
            {log.trace_id ? (
              <Row label={t("Trace")}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <button
                    type="button"
                    onClick={() => onOpenTrace(log.trace_id!)}
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    <RouteIcon className="h-3 w-3" />
                    <span className="font-mono">{log.trace_id}</span>
                  </button>
                  <Link
                    to={`/zeitstrahl/${encodeURIComponent(log.trace_id)}`}
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    <Activity className="h-3 w-3" />
                    {t("Im Zeitstrahl öffnen")}
                  </Link>
                </div>
              </Row>
            ) : null}
            {log.span_id ? <Row label={t("Span")}>{log.span_id}</Row> : null}
            {log.id != null ? <Row label={t("DB-ID")}>{log.id}</Row> : null}
          </dl>

          {hasPayload ? (
            <PayloadBlock
              mime={log.payload_mime!}
              size={log.payload_size ?? null}
              src={payloadSrc}
              onExpand={() => setLightbox(true)}
            />
          ) : null}

          {log.metadata ? <MetadataBlock raw={log.metadata} /> : null}
        </div>
      </aside>

      {lightbox && hasPayload ? (
        <Lightbox mime={log.payload_mime!} src={payloadSrc} onClose={() => setLightbox(false)} />
      ) : null}
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </>
  );
}

/** Grobe Payload-Kategorie aus dem MIME-Type. */
function payloadKind(mime: string): "image" | "video" | "audio" | "other" {
  const m = mime.toLowerCase();
  if (m.startsWith("image/") && m !== "image/svg+xml") return "image";
  if (m.startsWith("video/")) return "video";
  if (m.startsWith("audio/")) return "audio";
  return "other";
}

function PayloadBlock({
  mime,
  size,
  src,
  onExpand,
}: {
  mime: string;
  size: number | null;
  src: string;
  onExpand: () => void;
}) {
  const kind = payloadKind(mime);

  return (
    <div className="mt-5 rounded-md border border-border bg-muted/40">
      <div className="flex items-center justify-between px-3 py-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          <Paperclip className="h-3 w-3" /> {t("Payload")}
        </span>
        <span className="text-[11px] text-muted-foreground">
          {mime}
          {size != null ? ` · ${formatBytes(size)}` : ""}
        </span>
      </div>

      <div className="px-3 pb-3">
        {kind === "image" ? (
          <button
            type="button"
            onClick={onExpand}
            className="group relative block w-full overflow-hidden rounded-md border border-border"
            title={t("Großansicht öffnen")}
          >
            <img
              src={src}
              alt={t("Payload")}
              className="max-h-64 w-full bg-background object-contain"
            />
            <span className="absolute right-2 top-2 rounded bg-foreground/60 p-1 text-background opacity-0 transition-opacity group-hover:opacity-100">
              <Maximize2 className="h-3.5 w-3.5" />
            </span>
          </button>
        ) : kind === "video" ? (
          <div className="flex flex-col gap-2">
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video src={src} controls className="max-h-64 w-full rounded-md bg-black" />
            <button
              type="button"
              onClick={onExpand}
              className="inline-flex w-fit items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Maximize2 className="h-3.5 w-3.5" /> {t("Großansicht")}
            </button>
          </div>
        ) : kind === "audio" ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <audio src={src} controls className="w-full" />
        ) : (
          <a
            href={src}
            target="_blank"
            rel="noreferrer"
            download
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs text-foreground hover:bg-muted"
          >
            <Download className="h-3.5 w-3.5" /> {t("Datei herunterladen")}
          </a>
        )}
      </div>
    </div>
  );
}

/** Vollbild-Overlay für Bild/Video. Klick auf den Hintergrund schließt. */
function Lightbox({ mime, src, onClose }: { mime: string; src: string; onClose: () => void }) {
  const kind = payloadKind(mime);
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-2 md:p-6"
      onClick={onClose}
      role="dialog"
      aria-label={t("Payload-Großansicht")}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={t("Schließen")}
        className="absolute right-3 top-3 mt-[env(safe-area-inset-top,0px)] rounded-md p-2.5 text-white/80 hover:bg-white/10 hover:text-white md:right-4 md:top-4"
      >
        <X className="h-5 w-5" />
      </button>
      {kind === "video" ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
          src={src}
          controls
          autoPlay
          className="max-h-full max-w-full rounded-md"
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <img
          src={src}
          alt={t("Payload")}
          className="max-h-full max-w-full rounded-md object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      )}
    </div>
  );
}

function MetadataBlock({ raw }: { raw: string }) {
  let pretty = raw;
  try {
    pretty = JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    // war kein JSON, raw bleibt
  }
  return (
    <details className="mt-5 rounded-md border border-border bg-muted/40" open>
      <summary className="cursor-pointer px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {t("Metadata")}
      </summary>
      <pre className="max-h-[40vh] overflow-auto px-3 pb-3 font-mono text-xs">{pretty}</pre>
    </details>
  );
}
